import { useMemo } from 'react';
import CallConsumptionDisplay, { type BillableConsumptionBucket } from './CallConsumptionDisplay';

interface CampaignConsumptionDisplayProps {
  aiBilledSeconds?: number | null;
  aiGrossBilledSeconds?: number | null;
  aiRefundedSeconds?: number | null;
  telephonyBilledSeconds?: number | null;
  telephonyGrossBilledSeconds?: number | null;
  telephonyRefundedSeconds?: number | null;
  aiDurationSeconds?: number | null;
  telephonyDurationSeconds?: number | null;
  fallbackBilledSeconds?: number | null;
  fallbackDurationSeconds?: number | null;
  compact?: boolean;
}

export default function CampaignConsumptionDisplay({
  aiBilledSeconds,
  aiGrossBilledSeconds,
  aiRefundedSeconds,
  telephonyBilledSeconds,
  telephonyGrossBilledSeconds,
  telephonyRefundedSeconds,
  aiDurationSeconds,
  telephonyDurationSeconds,
  fallbackBilledSeconds,
  fallbackDurationSeconds,
  compact = true,
}: CampaignConsumptionDisplayProps) {
  const billableBuckets = useMemo<BillableConsumptionBucket[]>(() => {
    const normalized: BillableConsumptionBucket[] = [];
    const aiBilled = Math.max(Number(aiBilledSeconds || 0), 0);
    const aiGrossBilled = Math.max(Number((aiGrossBilledSeconds ?? aiBilledSeconds) || 0), 0);
    const aiRefunded = Math.max(Number(aiRefundedSeconds || 0), 0);
    const aiDuration = Math.max(Number(aiDurationSeconds || 0), 0);
    const telephonyBilled = Math.max(Number(telephonyBilledSeconds || 0), 0);
    const telephonyGrossBilled = Math.max(Number((telephonyGrossBilledSeconds ?? telephonyBilledSeconds) || 0), 0);
    const telephonyRefunded = Math.max(Number(telephonyRefundedSeconds || 0), 0);
    const telephonyDuration = Math.max(Number(telephonyDurationSeconds || 0), 0);

    if (aiBilled > 0 || aiDuration > 0) {
      normalized.push({
        key: 'ai',
        billedSeconds: aiBilled,
        grossBilledSeconds: aiGrossBilled,
        refundedSeconds: aiRefunded,
        effectiveSeconds: aiDuration,
      });
    }

    if (telephonyBilled > 0 || telephonyDuration > 0) {
      normalized.push({
        key: 'telephony',
        billedSeconds: telephonyBilled,
        grossBilledSeconds: telephonyGrossBilled,
        refundedSeconds: telephonyRefunded,
        effectiveSeconds: telephonyDuration,
      });
    }

    return normalized;
  }, [
    aiBilledSeconds,
    aiGrossBilledSeconds,
    aiRefundedSeconds,
    aiDurationSeconds,
    telephonyBilledSeconds,
    telephonyGrossBilledSeconds,
    telephonyRefundedSeconds,
    telephonyDurationSeconds,
  ]);

  return (
    <CallConsumptionDisplay
      billableBuckets={billableBuckets}
      fallbackBilledSeconds={fallbackBilledSeconds}
      fallbackDurationSeconds={fallbackDurationSeconds}
      compact={compact}
    />
  );
}
